/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Storage", description = "Learn more about what data Hightouch stores, whether with Hightouch or your own infrastructure, to power your syncs.", lastUpdatedDate = new Date(1792108800000), keywords = "kms, key management service, encryption at rest";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Storage",
        description: "Learn more about what data Hightouch stores, whether with Hightouch or your own infrastructure, to power your syncs.",
        lastUpdatedDate: "Fri Oct 16 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What data does Hightouch store?",
          "depth": 0,
          "id": "what-data-does-hightouch-store"
        }, {
          "value": "Change data capture",
          "depth": 1,
          "id": "change-data-capture"
        }, {
          "value": "Observability and debugging",
          "depth": 1,
          "id": "observability-and-debugging"
        }, {
          "value": "Managed storage",
          "depth": 0,
          "id": "managed-storage"
        }, {
          "value": "Data retention",
          "depth": 1,
          "id": "data-retention"
        }, {
          "value": "Self-hosted storage",
          "depth": 0,
          "id": "self-hosted-storage"
        }, {
          "value": "Changing your storage bucket",
          "depth": 0,
          "id": "changing-your-storage-bucket"
        }, {
          "value": "What to expect",
          "depth": 1,
          "id": "what-to-expect"
        }, {
          "value": "Amazon S3",
          "depth": 1,
          "id": "amazon-s3"
        }, {
          "value": "Google Cloud Storage",
          "depth": 1,
          "id": "google-cloud-storage"
        }, {
          "value": "Microsoft Azure Blob Storage",
          "depth": 1,
          "id": "microsoft-azure-blob-storage"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      ol: "ol",
      li: "li",
      strong: "strong",
      ul: "ul",
      h3: "h3",
      h4: "h4",
      code: "code",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      pre: "pre",
      span: "span",
      img: "img"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["Hightouch needs to store some data to power your syncs and to power features like the ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "sync debugger"
        }), ". You have full control over where this data is stored."]
      }), "\n", _jsx(_components.p, {
        children: "If you prefer more direct control or have specific security concerns, you can configure Hightouch to store this data in your own Amazon S3, Google Cloud Storage, or Azure Blob Storage account. If you prefer convenience, Hightouch can store this data in our own secure, encrypted infrastructure."
      }), "\n", _jsx(_components.h2, {
        id: "what-data-does-hightouch-store",
        children: "What data does Hightouch store?"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch stores data at-rest for two purposes:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/getting-started/concepts#change-data-capture-cdc",
            children: "Change data capture"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/syncs/debugger",
            children: "In-app observability and debugging"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Depending on your privacy and compliance needs, you can configure Hightouch to store ", _jsx(_components.strong, {
          children: "all"
        }), " data-at-rest within your Virtual Private Cloud (VPC), or in a secure, encrypted bucket."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["If you're on the ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Free, Starter, or Pro tier"
          }), ", Hightouch hosts and manages a secure encrypted bucket on your behalf. For more information, see the ", _jsx(_components.a, {
            href: "#managed-storage",
            children: "managed storage section"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["If you're on the ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier"
          }), ", you can self-host storage in your own infrastructure. For more information, see the ", _jsx(_components.a, {
            href: "#self-hosted-storage",
            children: "self-hosted storage section"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "change-data-capture",
        children: "Change data capture"
      }), "\n", _jsxs(_components.p, {
        children: ["To prevent making excessive API requests and send only necessary updates to your destinations, Hightouch uses a process called ", _jsx(_components.strong, {
          children: "change data capture"
        }), " (CDC) or ", _jsx(_components.strong, {
          children: "diffing"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "In this process, Hightouch stores query results and execution plans after each sync run. When the next sync run occurs, Hightouch uses these diff files to determine incremental changes that should be sent downstream."
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch stores the diff files in a secure encrypted bucket hosted by Hightouch. ", _jsx(_components.a, {
          href: "https://hightouch.com/pricing",
          children: "Business tier"
        }), " accounts can use the ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine",
          children: "Lightning sync engine"
        }), " to compute and stores diffs directly within their data warehouse."]
      }), "\n", _jsxs(_components.p, {
        children: ["To learn more about CDC, check out the ", _jsx(_components.a, {
          href: "/syncs/cdc",
          children: "change data capture docs"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "observability-and-debugging",
        children: "Observability and debugging"
      }), "\n", _jsxs(_components.p, {
        children: ["In addition to storing previous query results, Hightouch stores row-level log metadata including successes and failures, operations performed, and API request and response payloads. This data powers the ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "in-app debugger"
        }), " and can be stored either in your VPC or Hightouch's encrypted bucket."]
      }), "\n", _jsx(_components.h2, {
        id: "managed-storage",
        children: "Managed storage"
      }), "\n", _jsxs(_components.p, {
        children: ["If you're on a ", _jsx(_components.a, {
          href: "https://hightouch.com/pricing",
          children: "Free, Starter, or Pro plan"
        }), " Hightouch stores data-at-rest in a secure, encrypted, Hightouch-managed bucket. For workspaces running in a Hightouch AWS region, this is an Amazon S3 bucket. For workspaces running in a Hightouch Google Cloud region, this is a Google Cloud Storage bucket. No matter your region, all data in Hightouch managed buckets is encrypted at rest. If you require data-at-rest to live entirely in your VPC, see ", _jsx(_components.a, {
          href: "/security/storage/#self-hosted-storage",
          children: "self-hosted storage"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "data-retention",
        children: "Data retention"
      }), "\n", _jsxs(_components.p, {
        children: ["Data automatically expires from Hightouch-managed buckets after 30 days. If ", _jsx(_components.a, {
          href: "/syncs/cdc",
          children: "change data capture"
        }), " is done in Hightouch-managed buckets, syncs that have not run in over 30 days will require a ", _jsx(_components.strong, {
          children: "Full Resync"
        }), " or ", _jsx(_components.strong, {
          children: "Reset CDC"
        }), " sync since Hightouch depends on diffing files to detect changes in the data model."]
      }), "\n", _jsx(_components.h2, {
        id: "self-hosted-storage",
        children: "Self-hosted storage"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://hightouch.com/pricing",
          children: "Business tier"
        }), " customers can configure Hightouch to store all customer data-at-rest within their own external storage bucket or blob. Hightouch integrates with these cloud storage providers:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Amazon S3"
        }), "\n", _jsx(_components.li, {
          children: "Google Cloud Storage (GCS)"
        }), "\n", _jsx(_components.li, {
          children: "Microsoft Azure Blob Storage"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you choose to self-host your storage, Hightouch only processes data-in-transit. You can select any supported storage provider to store your data, regardless of your ", _jsx(_components.a, {
          href: "/security/regions",
          children: "Hightouch region"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "When hosting your own storage, Hightouch places full control over object lifecycle, security, and expiration into your hands.\nWe don't expire objects automatically or change your object encryption settings.\nEnsure that you've configured object expiration, encryption, and access control settings according to your needs."
      }), "\n", _jsx(_components.h4, {
        id: "recommended-architecture-and-security-practices",
        children: "Recommended architecture and security practices"
      }), "\n", _jsx(_components.p, {
        children: "You provide the bucket or container, and Hightouch manages how objects are laid out within it. You can't configure a prefix or path for Hightouch to use, and its internal layout can change. Control access and isolate environments at the bucket level, not by path."
      }), "\n", _jsx(_components.p, {
        children: "Use the following guidelines to reduce setup issues and simplify operations:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Scope IAM permissions to the specific bucket or container Hightouch uses, and avoid granting access to unrelated buckets or storage accounts."
        }), "\n", _jsxs(_components.li, {
          children: ["Use a dedicated bucket or container per environment (for example, separate ", _jsx(_components.code, {
            children: "prod"
          }), ", ", _jsx(_components.code, {
            children: "staging"
          }), ", and ", _jsx(_components.code, {
            children: "dev"
          }), " buckets) to avoid cross-environment access. If you need to isolate individual workspaces, give each its own bucket, since Hightouch can't restrict a workspace to a path within a shared bucket."]
        }), "\n", _jsx(_components.li, {
          children: "Co-locate storage with your warehouse and destinations when possible to reduce latency and egress costs."
        }), "\n", _jsxs(_components.li, {
          children: ["Prefer private connectivity over public internet paths when available: ", _jsx(_components.a, {
            href: "/security/aws/privatelink",
            children: "AWS PrivateLink"
          }), ", ", _jsx(_components.a, {
            href: "/security/azure/private-link",
            children: "Azure Private Link"
          }), ", or ", _jsx(_components.a, {
            href: "/security/gcp/private-service-connect",
            children: "Google Private Service Connect"
          }), ". If using public endpoints, maintain strict IP allowlists."]
        }), "\n", _jsx(_components.li, {
          children: "Use encryption at rest with your standard key-management policy (for example SSE-KMS, CMEK, or customer-managed keys) and ensure key policies grant your Hightouch principal the required access."
        }), "\n", _jsx(_components.li, {
          children: "Apply lifecycle rules that auto-expire objects according to your retention policy, and enable storage access logging when required for audits."
        }), "\n", _jsxs(_components.li, {
          children: ["After initial setup or credential rotation, run a small validation sync and confirm data is accessible in the ", _jsx(_components.a, {
            href: "/syncs/debugger",
            children: "sync debugger"
          }), " before broad rollout."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Setting up self-hosted storage disrupts the ", _jsx(_components.a, {
          href: "/syncs/cdc",
          children: "change data capture"
        }), " process for active syncs.\nTo reset it, after you've configured self-hosted storage, you need to ", _jsx(_components.a, {
          href: "/syncs/overview#resync-full-query",
          children: "trigger a full resync"
        }), " or ", _jsx(_components.a, {
          href: "/syncs/overview#reset-cdc",
          children: "reset cdc sync"
        }), " for all existing syncs that previously ran with ", _jsx(_components.a, {
          href: "#managed-storage",
          children: "Hightouch-managed storage"
        }), ".\nMake sure that all your syncs satisfy the ", _jsx(_components.a, {
          href: "/syncs/overview#full-resync-prerequisites",
          children: "full resync prerequisites"
        }), " before setting up self-hosted storage.\nDon't hesitate to ", _jsx(PylonLink, {
          pylonText: "Hello, I have a question about setting up self-hosted storage.",
          linkText: "reach out"
        }), " if you have any doubts or concerns."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Once you've run a sync after setting up a custom storage bucket, you can't\nmake further changes to your storage configuration self-serve, including\ndisabling it. This is because changing your storage configuration is\ndisruptive to active syncs and CDC state. If you need to change your bucket,\nsee ", _jsx(_components.a, {
            href: "#changing-your-storage-bucket",
            children: "Changing your storage bucket"
          }), " below."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "changing-your-storage-bucket",
        children: "Changing your storage bucket"
      }), "\n", _jsxs(_components.p, {
        children: ["If you haven't yet configured an external bucket, you can set one up self-serve by following the provider-specific instructions ", _jsx(_components.a, {
          href: "#self-hosted-storage",
          children: "above"
        }), ". The setup is self-serve, but can be disruptive to active syncs — review the ", _jsx(_components.a, {
          href: "#self-hosted-storage",
          children: "CDC impact"
        }), " and consider reaching out to support for monitoring if you have a large number of syncs."]
      }), "\n", _jsxs(_components.p, {
        children: ["Once syncs have run with an external bucket configured, changing your storage configuration — for example, moving to a new AWS account, switching regions, or pointing to a different bucket — is a ", _jsx(_components.strong, {
          children: "support-assisted operation"
        }), ". There is no self-serve way to change your bucket configuration after syncs have run."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(PylonLink, {
          pylonText: "Hello, I need to change or migrate my external storage bucket.",
          linkText: "Contact Hightouch support"
        }), " to start the process. To help the team plan your migration efficiently, include\nthe following information in your request:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Scope:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Workspace name(s) and URL(s) affected"
        }), "\n", _jsx(_components.li, {
          children: "Whether the workspace is production or non-production"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "New bucket details:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Cloud provider and region (for example, ", _jsx(_components.code, {
            children: "AWS us-east-1"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Bucket or container name (for example, ", _jsx(_components.code, {
            children: "my-company-hightouch-prod"
          }), ")"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Access credentials:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["For AWS: the IAM role ARN (and external ID, if required) with the ", _jsx(_components.a, {
            href: "#authenticate-hightouch-with-aws",
            children: "permissions listed above"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["For GCS: the service account email and confirmation that it has the required permissions (", _jsx(_components.code, {
            children: "storage.objects.list"
          }), ", ", _jsx(_components.code, {
            children: "storage.objects.create"
          }), ", ", _jsx(_components.code, {
            children: "storage.objects.get"
          }), ", ", _jsx(_components.code, {
            children: "storage.objects.delete"
          }), ", ", _jsx(_components.code, {
            children: "storage.buckets.get"
          }), ")"]
        }), "\n", _jsx(_components.li, {
          children: "For Azure: the storage principal and scope"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Feature usage:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Whether you're using the ", _jsx(_components.a, {
            href: "/syncs/lightning-sync-engine",
            children: "Lightning sync engine"
          }), ", ", _jsx(_components.a, {
            href: "/ai-decisioning/overview",
            children: "AI Decisioning"
          }), ", or other features that store artifacts in the bucket"]
        }), "\n", _jsxs(_components.li, {
          children: ["Whether you're using ", _jsx(_components.strong, {
            children: "Snowflake-integrated features"
          }), " such as ", _jsx(_components.strong, {
            children: "Snowflake Warehouse Export"
          }), " — these rely on Snowflake external volumes and stages that also need to be updated to point at the new bucket"]
        }), "\n", _jsx(_components.li, {
          children: "Any lifecycle policies on the old or new bucket that automatically delete objects — particularly planner-state files, which can trigger unexpected full resyncs if expired"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Maintenance window:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "A preferred time window (ideally during low traffic) when syncs can be paused and the configuration updated"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "what-to-expect",
        children: "What to expect"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch will update your workspace's storage configuration to point to the new bucket and validate connectivity. The impact on your syncs depends on the sync engine:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Lightning sync engine:"
          }), " CDC state is stored primarily in your data warehouse, not in the bucket. Changing buckets generally does not require re-seeding CDC, but historical debugger logs from the old bucket won't carry over to the new one."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Standard sync engine:"
          }), " CDC relies on snapshots and diff files stored in the bucket. After a bucket change, each affected sync will need a one-time ", _jsx(_components.a, {
            href: "/syncs/overview#resync-full-query",
            children: "full resync"
          }), " or ", _jsx(_components.a, {
            href: "/syncs/overview#reset-cdc",
            children: "CDC reset"
          }), " to establish a clean baseline in the new bucket. Plan for higher warehouse and API load during these initial runs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "AI Decisioning:"
          }), " The bucket stores long-lived ML artifacts and interaction logs. Migrating may require copying these artifacts to the new bucket, or AI Decisioning will rebuild state from your warehouse over time. Call this out in your support request so the team can plan accordingly."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Snowflake-integrated features (Snowflake Warehouse Export and others):"
          }), " If you use features that rely on Snowflake external volumes or stages, the Hightouch team will also update the associated storage integrations to point at the new bucket. Make sure the new bucket's cloud and region are compatible with your Snowflake account."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "After the configuration change, run a small test sync and verify that new artifacts and logs appear in the new bucket before resuming all workloads."
      }), "\n", _jsx(_components.h3, {
        id: "amazon-s3",
        children: "Amazon S3"
      }), "\n", _jsxs(_components.p, {
        children: ["Before getting started, ", _jsx(_components.a, {
          href: "/security/aws",
          children: "connect Hightouch with your Amazon Web Services account"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "create-your-s3-bucket",
        children: "Create your S3 bucket"
      }), "\n", _jsxs(_components.p, {
        children: ["In Amazon S3, ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/AmazonS3/latest/userguide/create-bucket-overview.html",
          children: "create your bucket"
        }), ". We recommend the name ", _jsx(_components.code, {
          children: "<company>-hightouch"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Make sure to:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Block all public access to the bucket."
        }), "\n", _jsxs(_components.li, {
          children: ["Enable Amazon S3 key encryption (SSE-S3). If using ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/AmazonS3/latest/userguide/UsingKMSEncryption.html",
            children: "SSE-KMS"
          }), " for encryption, you may need to ", _jsx(_components.a, {
            href: "https://repost.aws/knowledge-center/cross-account-access-denied-error-s3",
            children: "update your IAM policies"
          }), " to grant Hightouch access."]
        }), "\n", _jsx(_components.li, {
          children: "Prefer bucket policies with IAM condition scoping over ACL-based access controls."
        }), "\n", _jsx(_components.li, {
          children: "Disable bucket versioning."
        }), "\n", _jsxs(_components.li, {
          children: ["Configure your ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-lifecycle-mgmt.html",
            children: "bucket object lifecycle"
          }), ", to enhance security and cut down on costs."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "authenticate-hightouch-with-aws",
        children: "Authenticate Hightouch with AWS"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports authenticating with AWS using Cross-account roles (via STS AssumeRole), or with an Access Key ID / Secret Access Key that you provide. We strongly encourage you to use Cross-account roles, as it doesn't require Hightouch to hold any of your secrets."
      }), "\n", _jsxs(_components.p, {
        children: ["To set up your Hightouch AWS credential, follow our ", _jsx(_components.a, {
          href: "/security/aws",
          children: "connection instructions"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch needs the following IAM actions to store and retrieve items from your bucket:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Action"
            }), _jsx(_components.th, {
              align: "left",
              children: "Details"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "s3:GetObject"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants permission to retrieve objects from Amazon S3"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "s3:GetObjectVersion"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants permission to read versioned objects when applicable"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "s3:PutObject"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants permission to add an object to a bucket"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "s3:ListBucket"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants permission to list some or all the objects in an Amazon S3 bucket (up to 1000)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "s3:GetBucketLocation"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants permission to read bucket region metadata"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "s3:DeleteObject"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants permission to delete objects from an Amazon S3 bucket"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["You can use the following JSON sample to grant Hightouch access to your bucket. Replace ", _jsx(_components.code, {
          children: "${bucketName}"
        }), " with your bucket name."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2012-10-17\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Statement\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ListBucket\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:ListBucket\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:GetBucketLocation\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:s3:::${bucketName}\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ObjectAccess\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:GetObject\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:GetObjectVersion\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:PutObject\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:DeleteObject\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:s3:::${bucketName}/*\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "configure-your-bucket-in-hightouch",
        children: "Configure your bucket in Hightouch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, on the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/settings/storage?from=docs",
            children: [_jsx(_components.strong, {
              children: "Storage"
            }), " tab"]
          }), " of the ", _jsx(_components.strong, {
            children: "Settings"
          }), " page, select ", _jsx(_components.strong, {
            children: "Amazon S3"
          }), " as the ", _jsx(_components.strong, {
            children: "Cloud storage provider"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select your ", _jsx(_components.strong, {
            children: "AWS region"
          }), ", enter your ", _jsx(_components.strong, {
            children: "Bucket name"
          }), ", and select the ", _jsx(_components.strong, {
            children: "AWS credentials"
          }), " you previously set up."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "security/security-storage-aws-1-configure-bucket.png",
          alt: "Hightouch app screenshot"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once you save your settings, your new syncs automatically start using your bucket. ", _jsx(_components.a, {
          href: "/syncs/create-your-first-sync",
          children: "Run a few syncs"
        }), " and visit your S3 bucket to check files are saving there.\nDon't hesitate to ", _jsx(PylonLink, {
          pylonText: "Hello, I have questions about storage in my S3 bucket.",
          linkText: "get in touch"
        }), " if you have any questions."]
      }), "\n", _jsx(_components.h4, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsxs(_components.p, {
        children: ["If you receive a ", _jsx(_components.code, {
          children: "CredentialsError: Missing credentials in configuration"
        }), " error, refer to the ", _jsx(_components.a, {
          href: "/security/aws#credentialserror-missing-credentials-in-configuration",
          children: "troubleshooting tips"
        }), " in the AWS integration docs."]
      }), "\n", _jsx(_components.h3, {
        id: "google-cloud-storage",
        children: "Google Cloud Storage"
      }), "\n", _jsxs(_components.p, {
        children: ["Before getting started, ", _jsx(_components.a, {
          href: "/security/gcp",
          children: "connect Hightouch with your Google Cloud account"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "create-a-bucket",
        children: "Create a bucket"
      }), "\n", _jsxs(_components.p, {
        children: ["In the Google Cloud console, ", _jsx(_components.a, {
          href: "https://cloud.google.com/storage/docs/creating-buckets",
          children: "create a new bucket"
        }), ". We recommend the name ", _jsx(_components.code, {
          children: "<company>-hightouch-bucket"
        }), ". Copy the bucket name and save it for later.\nConfigure your ", _jsx(_components.a, {
          href: "https://cloud.google.com/storage/docs/lifecycle",
          children: "bucket object lifecycle"
        }), ", to enhance security and cut down on costs."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Make sure to:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Enable ", _jsx(_components.a, {
            href: "https://cloud.google.com/storage/docs/uniform-bucket-level-access",
            children: "Uniform bucket-level access"
          }), " and manage access through IAM policies instead of object ACLs."]
        }), "\n", _jsx(_components.li, {
          children: "Use CMEK where required by your compliance controls, and ensure your service account has access to the key."
        }), "\n", _jsxs(_components.li, {
          children: ["If you use ", _jsx(_components.a, {
            href: "https://cloud.google.com/vpc-service-controls/docs/overview",
            children: "VPC Service Controls"
          }), ", include the bucket and service account in the same perimeter to avoid access denials."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "authenticate-with-google-cloud",
        children: "Authenticate with Google Cloud"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports authenticating with GCP using Hightouch-managed service accounts, or by using a service account that you control."
      }), "\n", _jsxs(_components.p, {
        children: ["To set up your Hightouch GCP credential, follow our ", _jsx(_components.a, {
          href: "/security/gcp",
          children: "connection instructions"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch needs the following IAM permissions to store and retrieve items from your bucket:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Permission"
            }), _jsx(_components.th, {
              align: "left",
              children: "Details"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "storage.objects.list"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants access to view objects and their metadata, excluding ACLs. Can also list the objects in a bucket."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "storage.objects.create"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants permission to create, replace, and delete objects; list objects in a bucket; read object metadata when listing (excluding IAM policies); and read bucket metadata, excluding IAM policies."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "storage.objects.get"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants access to view objects and their metadata, excluding ACLs. Can also list the objects in a bucket."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "storage.objects.delete"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Grants permission to overwrite existing objects."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "configure-your-bucket-in-hightouch-1",
        children: "Configure your bucket in Hightouch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, on the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/settings/storage?from=docs",
            children: [_jsx(_components.strong, {
              children: "Storage"
            }), " tab"]
          }), " of the ", _jsx(_components.strong, {
            children: "Settings"
          }), " page, select ", _jsx(_components.strong, {
            children: "Google Cloud Storage"
          }), " as the ", _jsx(_components.strong, {
            children: "Cloud storage provider"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter the ", _jsx(_components.strong, {
            children: "Project ID"
          }), " and ", _jsx(_components.strong, {
            children: "Bucket name"
          }), " and select the ", _jsx(_components.strong, {
            children: "Google Cloud credentials"
          }), " you previously set up."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/gcp/use-1-external-storage.png",
          alt: "Hightouch app screenshot"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once you save your settings, your new syncs automatically start using your bucket. ", _jsx(_components.a, {
          href: "/syncs/create-your-first-sync",
          children: "Run a few syncs"
        }), " and visit your Google Cloud bucket to check files are saving there.\nDon't hesitate to ", _jsx(PylonLink, {
          pylonText: "Hello, I have questions about storage in my GCP bucket.",
          linkText: "get in touch"
        }), " if you have any questions."]
      }), "\n", _jsx(_components.h3, {
        id: "microsoft-azure-blob-storage",
        children: "Microsoft Azure Blob Storage"
      }), "\n", _jsxs(_components.p, {
        children: ["Before getting started, ", _jsx(_components.a, {
          href: "/security/azure",
          children: "connect Hightouch with your Azure account"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "create-a-container",
        children: "Create a container"
      }), "\n", _jsxs(_components.p, {
        children: ["In your Azure portal, ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/storage/blobs/storage-quickstart-blobs-portal#create-a-container",
          children: "create a container"
        }), ". We recommend the name ", _jsx(_components.code, {
          children: "<company>-hightouch-container"
        }), ". Copy the container name and save it for later.\nConfigure your ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/storage/blobs/lifecycle-management-overview",
          children: "storage lifecycle"
        }), ", to enhance security and cut down on costs."]
      }), "\n", _jsx(_components.h4, {
        id: "authenticate-with-azure",
        children: "Authenticate with Azure"
      }), "\n", _jsxs(_components.p, {
        children: ["Grant the app two roles: the ", _jsx(_components.strong, {
          children: "Storage Blob Data Contributor"
        }), " role for the storage account (or container), and the ", _jsx(_components.strong, {
          children: "Storage Blob Delegator"
        }), " role at the storage-account level. The delegator role is required for file downloads and previews (such as Data Agents and AI Decisioning CSV exports), which Hightouch serves through signed links — without it, uploads succeed but downloads fail with ", _jsx(_components.code, {
          children: "AuthorizationPermissionMismatch"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Use a Service Principal with RBAC over long-lived SAS tokens when possible, and rotate client secrets or certificates on your normal security cadence."
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to create a custom role with more granular permissions, Hightouch needs the following IAM permissions to store and retrieve items from your Blob Storage ", _jsx(_components.strong, {
          children: "Container"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "\"actions\".\"Microsoft.Storage/storageAccounts/blobServices/containers/read\""
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "\"dataActions\".\"Microsoft.Storage/storageAccounts/blobServices/containers/blobs/add/action\""
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "\"dataActions\".\"Microsoft.Storage/storageAccounts/blobServices/containers/blobs/write\""
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "\"dataActions\".\"Microsoft.Storage/storageAccounts/blobServices/containers/blobs/delete\""
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Also grant the following on the storage ", _jsx(_components.strong, {
          children: "Account"
        }), " (required for file downloads and previews):"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "\"actions\".\"Microsoft.Storage/storageAccounts/blobServices/generateUserDelegationKey/action\""
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're using ADLS Gen2 (hierarchical namespace enabled), ensure list operations target stable folder prefixes (for example with trailing ", _jsx(_components.code, {
          children: "/"
        }), ") and validate that your processing handles directory entries correctly."]
      }), "\n", _jsx(_components.h4, {
        id: "configure-your-container-in-hightouch",
        children: "Configure your container in Hightouch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Hightouch, on the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/settings/storage?from=docs",
            children: [_jsx(_components.strong, {
              children: "Storage"
            }), " tab"]
          }), " of the ", _jsx(_components.strong, {
            children: "Settings"
          }), " page, select ", _jsx(_components.strong, {
            children: "Microsoft Azure"
          }), " as the ", _jsx(_components.strong, {
            children: "Cloud storage provider"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter the ", _jsx(_components.strong, {
            children: "Account name"
          }), " and ", _jsx(_components.strong, {
            children: "Container name"
          }), " from the Azure portal (see ", _jsx(_components.a, {
            href: "/security/azure",
            children: "connecting Hightouch with your Azure account"
          }), "), and select the ", _jsx(_components.strong, {
            children: "Azure credentials"
          }), " you previously set up."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/azure/use-1-external-storage.png",
          alt: "Hightouch app screenshot"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once you save your settings, your new syncs automatically start using your container. ", _jsx(_components.a, {
          href: "/syncs/create-your-first-sync",
          children: "Run a few syncs"
        }), " and visit your Blob Storage container to check files are saving there.\nDon't hesitate to ", _jsx(PylonLink, {
          pylonText: "Hello, I have questions about storage in my Azure Blob Storage container.",
          linkText: "get in touch"
        }), " if you have any questions."]
      }), "\n", _jsx(_components.h4, {
        id: "common-errors-1",
        children: "Common errors"
      }), "\n", _jsx(_components.p, {
        children: "Your Azure storage configuration may fail to save for a few reasons:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "invalid_client"
          }), ": Check that you've provided your client secret ", _jsx(_components.strong, {
            children: "value"
          }), ", not client secret ID. See the ", _jsx(_components.a, {
            href: "/security/azure#create-an-app-registration",
            children: "configuration docs"
          }), " for more information."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/azure/azure-app-secret-value.png",
          alt: "Azure Portal screenshot"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "This request is not authorized to perform this operation"
          }), ": Check that Hightouch has ", _jsx(_components.a, {
            href: "#authenticate-with-azure",
            children: "adequate access"
          }), " to your storage account. If you've configured your storage account's ", _jsx(_components.strong, {
            children: "Network access"
          }), " to be ", _jsx(_components.strong, {
            children: "from selected virtual networks and IP addresses"
          }), ", be sure to add Hightouch's ", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "IP addresses"
          }), " to your ", _jsx(_components.a, {
            href: "https://learn.microsoft.com/en-us/azure/storage/common/storage-network-security?tabs=azure-portal",
            children: "network rules"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/azure/azure-networking.png",
          alt: "Azure Portal screenshot"
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
